<!-- The legal HTML is generated from controlled DOCX files and sanitized with DOMPurify. -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <f7-page
    name="LegalDocumentsInternal"
    class="legal-internal-page"
    :lang="locale"
  >
    <main class="legal-internal-layout">
      <section
        class="legal-internal-hero"
        aria-labelledby="legal-internal-title"
      >
        <p class="legal-internal-eyebrow">
          {{ copy.legalInformation }}
        </p>
        <h1 id="legal-internal-title">{{ copy.pageTitle }}</h1>
      </section>

      <div class="legal-internal-content">
        <nav
          class="legal-internal-index"
          :aria-label="copy.legalIndexLabel"
        >
          <p class="legal-internal-index__title">
            {{ copy.onThisPage }}
          </p>
          <ol>
            <li v-for="document in documents" :key="document.id">
              <a
                :href="`#${document.id}`"
                @click.prevent="scrollToSection(document.id)"
              >
                {{ document.shortTitle }}
              </a>
            </li>
          </ol>
        </nav>

        <div
          class="legal-internal-documents"
          @click="handleLegalContentClick"
        >
          <article
            v-for="(document, index) in documents"
            :id="document.id"
            :key="document.id"
            class="legal-internal-document"
            :aria-labelledby="`${document.id}-title`"
          >
            <p class="legal-internal-document__number">
              {{ copy.document }} {{ index + 1 }} {{ copy.of }}
              {{ documents.length }}
            </p>
            <h2 :id="`${document.id}-title`">
              {{ document.title }}
            </h2>

            <nav
              class="legal-internal-section-index"
              :aria-label="`${copy.contentsOf} ${document.shortTitle}`"
            >
              <p>{{ copy.documentContents }}</p>
              <ul>
                <li
                  v-for="section in document.sections"
                  :key="`${section.id}-index`"
                >
                  <a
                    :href="`#${section.id}`"
                    @click.prevent="scrollToSection(section.id)"
                  >
                    {{ section.title }}
                  </a>
                </li>
              </ul>
            </nav>

            <section
              v-for="section in document.sections"
              :id="section.id"
              :key="section.id"
              class="legal-internal-section"
              :aria-labelledby="`${section.id}-title`"
            >
              <h3 :id="`${section.id}-title`">
                {{ section.title }}
              </h3>

              <template v-for="(block, blockIndex) in section.blocks">
                <p
                  v-if="block.type === 'paragraph'"
                  :key="`${section.id}-paragraph-${blockIndex}`"
                  v-html="sanitizeLegalHtml(block.html)"
                ></p>

                <h4
                  v-else-if="block.type === 'subheading'"
                  :key="`${section.id}-subheading-${blockIndex}`"
                  v-html="sanitizeLegalHtml(block.html)"
                ></h4>

                <ul
                  v-else-if="block.type === 'list'"
                  :key="`${section.id}-list-${blockIndex}`"
                >
                  <li
                    v-for="(item, itemIndex) in block.items"
                    :key="`${section.id}-list-${blockIndex}-${itemIndex}`"
                    v-html="sanitizeLegalHtml(item)"
                  ></li>
                </ul>

                <div
                  v-else-if="block.type === 'table'"
                  :key="`${section.id}-table-${blockIndex}`"
                  class="legal-internal-table-wrapper"
                >
                  <table>
                    <tbody>
                      <tr
                        v-for="(row, rowIndex) in block.rows"
                        :key="`${section.id}-row-${rowIndex}`"
                      >
                        <td
                          v-for="(cell, cellIndex) in row"
                          :key="`${section.id}-cell-${rowIndex}-${cellIndex}`"
                          v-html="sanitizeLegalHtml(cell)"
                        ></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </section>
          </article>
        </div>
      </div>
    </main>

    <button
      v-show="showBackToTop"
      type="button"
      class="legal-internal-back-to-top"
      :aria-label="copy.backToTopLabel"
      :title="copy.backToTop"
      @click="scrollToTop"
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M5 15l7-7 7 7"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </f7-page>
</template>

<script>
import { mapState } from 'vuex';
import LegalDocumentsContentEs from '@/json/legalDocuments.json';
import LegalDocumentsContentEn from '@/json/legalDocuments.en.json';
import LegalDocumentsContentFr from '@/json/legalDocuments.fr.json';
import LegalDocumentsContentIt from '@/json/legalDocuments.it.json';
import LegalDocumentsContentPt from '@/json/legalDocuments.pt.json';
import { getLegalDocumentsLocale } from '@/js/legalDocumentsLocale';
import { canonicalizeLegalHash } from '@/js/legalDocumentsRoutes';
import DOMPurify from 'dompurify';

const spanishLegalDocumentLabels = {
  'privacy-policy': {
    shortTitle: 'Política de privacidad',
    title: 'Política de privacidad de Besafer',
  },
  'terms-of-use': {
    shortTitle: 'Condiciones de uso',
    title: 'Condiciones de uso de Besafer',
  },
  'cookie-policy': {
    shortTitle: 'Política de cookies',
    title: 'Política de cookies y tecnologías similares de Besafer',
  },
  'information-security-policy': {
    shortTitle: 'Política de seguridad',
    title:
      'Política de seguridad de la información para clientes de Besafer',
  },
  'legal-notice': {
    shortTitle: 'Aviso legal',
    title: 'Aviso legal de Besafer',
  },
};

const pageCopies = {
  es: {
    legalInformation: 'Información legal',
    pageTitle: 'Documentos legales de Besafer',
    legalIndexLabel: 'Índice de documentos legales',
    onThisPage: 'En esta página',
    document: 'Documento',
    of: 'de',
    contentsOf: 'Contenido de',
    documentContents: 'Contenido del documento',
    backToTopLabel: 'Volver al principio de la página',
    backToTop: 'Volver arriba',
  },
  en: {
    legalInformation: 'Legal information',
    pageTitle: 'Besafer legal documents',
    legalIndexLabel: 'Index of legal documents',
    onThisPage: 'On this page',
    document: 'Document',
    of: 'of',
    contentsOf: 'Contents of',
    documentContents: 'Document contents',
    backToTopLabel: 'Return to the top of the page',
    backToTop: 'Back to top',
  },
  fr: {
    legalInformation: 'Informations légales',
    pageTitle: 'Documents légaux de Besafer',
    legalIndexLabel: 'Index des documents légaux',
    onThisPage: 'Sur cette page',
    document: 'Document',
    of: 'sur',
    contentsOf: 'Contenu de',
    documentContents: 'Contenu du document',
    backToTopLabel: 'Revenir en haut de la page',
    backToTop: 'Retour en haut',
  },
  it: {
    legalInformation: 'Informazioni legali',
    pageTitle: 'Documenti legali di Besafer',
    legalIndexLabel: 'Indice dei documenti legali',
    onThisPage: 'In questa pagina',
    document: 'Documento',
    of: 'di',
    contentsOf: 'Contenuto di',
    documentContents: 'Contenuto del documento',
    backToTopLabel: "Torna all'inizio della pagina",
    backToTop: 'Torna su',
  },
  pt: {
    legalInformation: 'Informações legais',
    pageTitle: 'Documentos legais da Besafer',
    legalIndexLabel: 'Índice dos documentos legais',
    onThisPage: 'Nesta página',
    document: 'Documento',
    of: 'de',
    contentsOf: 'Conteúdo de',
    documentContents: 'Conteúdo do documento',
    backToTopLabel: 'Voltar ao início da página',
    backToTop: 'Voltar ao início',
  },
};

const legalDocumentsByLocale = {
  es: LegalDocumentsContentEs.map((document) => ({
    ...document,
    ...spanishLegalDocumentLabels[document.id],
  })),
  en: LegalDocumentsContentEn,
  fr: LegalDocumentsContentFr,
  it: LegalDocumentsContentIt,
  pt: LegalDocumentsContentPt,
};

export default {
  name: 'LegalDocumentsInternalPage',
  data() {
    return {
      showBackToTop: false,
      scrollContainer: null,
    };
  },
  computed: {
    ...mapState('user', ['languageInterface']),
    locale() {
      return getLegalDocumentsLocale(this.languageInterface);
    },
    copy() {
      return pageCopies[this.locale];
    },
    documents() {
      return legalDocumentsByLocale[this.locale];
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollContainer = this.$el.querySelector('.page-content');

      if (this.scrollContainer) {
        this.scrollContainer.addEventListener(
          'scroll',
          this.handleScroll,
          { passive: true }
        );
      }

      this.scrollToInitialSection();
    });
  },
  beforeDestroy() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener(
        'scroll',
        this.handleScroll
      );
    }
  },
  methods: {
    sanitizeLegalHtml(html) {
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['a', 'br', 'em', 'strong', 'u'],
        ALLOWED_ATTR: ['href', 'rel', 'target'],
      });
    },
    handleLegalContentClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      event.preventDefault();
      this.scrollToSection(link.getAttribute('href'));
    },
    handleScroll() {
      this.showBackToTop =
        (this.scrollContainer?.scrollTop || 0) > 400;
    },
    scrollToInitialSection() {
      const currentRoute =
        this.$f7?.views?.main?.router?.currentRoute;
      const currentPath = currentRoute?.path;
      // Los enlaces del banner autenticado entran por ?section=... para que
      // Menu pueda seguir reconociendo /legal-documents-internal como ruta
      // simple y mostrar Volver, independientemente del documento abierto.
      const requestedSection =
        currentRoute?.query?.section ||
        (currentRoute?.hash || '').replace(/^#/, '');

      if (requestedSection) {
        window.setTimeout(() => {
          this.scrollToSection(requestedSection);
        }, 0);
        return;
      }

      this.scrollToTop();

      if (currentPath === '/legal-notice-internal') {
        window.setTimeout(() => {
          this.scrollToSection('legal-notice');
        }, 0);
      }
    },
    scrollToSection(sectionId) {
      const canonicalId = canonicalizeLegalHash(sectionId);
      const section = this.$el.querySelector(`#${canonicalId}`);
      if (!section) return;

      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    scrollToTop() {
      if (!this.scrollContainer) return;

      this.scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.legal-internal-page {
  --legal-text: var(--his-berenjena, #2f4155);
  --legal-blue: var(--his-mora, #04509f);
  --legal-dark-blue: var(--his-arandano, #00346a);
  background: var(--his-mora-white, #ebf2fa);
  color: var(--legal-text);
}

.legal-internal-page :deep(.page-content) {
  height: 100%;
  padding: 0 !important;
  background: var(--his-mora-white, #ebf2fa);
  overflow-y: auto;
  scroll-behavior: smooth;
}

.legal-internal-layout {
  width: calc(100% - 48px);
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px 0 64px;
  box-sizing: border-box;
}

.legal-internal-hero {
  max-width: 820px;
  margin-bottom: 28px;
}

.legal-internal-eyebrow,
.legal-internal-document__number {
  margin: 0 0 10px;
  color: var(--legal-blue);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.legal-internal-hero h1 {
  margin: 0;
  color: var(--legal-dark-blue);
  font-size: 36px;
  line-height: 1.15;
  letter-spacing: 0;
}

.legal-internal-content {
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  gap: 32px;
  align-items: start;
}

.legal-internal-index {
  position: sticky;
  top: 16px;
  padding: 22px;
  background: #fff;
  border-left: 4px solid var(--legal-blue);
  border-radius: 0 8px 8px 0;
  box-shadow: 0 4px 18px rgba(0, 52, 106, 0.05);
}

.legal-internal-index__title {
  margin: 0 0 14px;
  color: var(--legal-dark-blue);
  font-size: 15px;
  font-weight: 700;
}

.legal-internal-index ol {
  margin: 0;
  padding-left: 20px;
}

.legal-internal-index li + li {
  margin-top: 12px;
}

.legal-internal-index a {
  color: var(--legal-text);
  line-height: 1.45;
  text-decoration: none;
}

.legal-internal-index a:hover,
.legal-internal-index a:focus-visible {
  color: var(--legal-blue);
  text-decoration: underline;
}

.legal-internal-documents {
  min-width: 0;
}

.legal-internal-document {
  scroll-margin-top: 16px;
  padding: 34px clamp(28px, 4vw, 56px);
  background: #fff;
  border: 1px solid var(--his-disabled, #e5eef7);
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 52, 106, 0.06);
}

.legal-internal-document + .legal-internal-document {
  margin-top: 24px;
}

.legal-internal-document h2 {
  margin: 0 0 20px;
  color: var(--legal-dark-blue);
  font-size: 28px;
  line-height: 1.25;
}

.legal-internal-section-index {
  margin: 28px 0 36px;
  padding: 22px 24px;
  background: var(--his-arandano-white, #f1f8ff);
  border: 1px solid var(--his-mora-tint, #d3e3f5);
  border-radius: 8px;
}

.legal-internal-section-index p {
  margin: 0 0 14px !important;
  color: var(--legal-dark-blue);
  font-size: 14px !important;
  font-weight: 700;
}

.legal-internal-section-index ul {
  margin: 0;
  padding: 0;
  column-count: 2;
  column-gap: 32px;
  list-style: none;
}

.legal-internal-section-index li {
  break-inside: avoid;
  margin-bottom: 9px;
}

.legal-internal-section-index a {
  color: var(--legal-blue);
  font-size: 14px;
  line-height: 1.45;
  text-decoration: none;
}

.legal-internal-section-index a:hover,
.legal-internal-section-index a:focus-visible {
  text-decoration: underline;
}

.legal-internal-section {
  min-width: 0;
  scroll-margin-top: 16px;
  padding-top: 30px;
  border-top: 1px solid var(--his-disabled, #e5eef7);
}

.legal-internal-section + .legal-internal-section {
  margin-top: 30px;
}

.legal-internal-section h3 {
  width: 100%;
  max-width: none;
  margin: 0 0 18px;
  color: var(--legal-dark-blue);
  font-size: 22px;
  line-height: 1.35;
}

.legal-internal-section h4 {
  width: 100%;
  max-width: none;
  margin: 24px 0 10px;
  color: var(--legal-text);
  font-size: 16px;
  line-height: 1.45;
}

.legal-internal-section p {
  width: 100%;
  max-width: none;
  margin: 0;
  font-size: 15px;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.legal-internal-section p + p {
  margin-top: 14px;
}

.legal-internal-section ul {
  width: 100%;
  max-width: none;
  margin: 14px 0 18px;
  padding-left: 25px;
  box-sizing: border-box;
}

.legal-internal-section li {
  padding-left: 4px;
  font-size: 15px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.legal-internal-section li::marker {
  color: var(--legal-blue);
}

.legal-internal-table-wrapper {
  width: 100%;
  margin: 18px 0;
  overflow-x: auto;
  border: 1px solid var(--his-disabled, #e5eef7);
  border-radius: 8px;
}

.legal-internal-table-wrapper table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
  background: #fff;
}

.legal-internal-table-wrapper td {
  padding: 12px 14px;
  border: 1px solid var(--his-disabled, #e5eef7);
  font-size: 14px;
  line-height: 1.5;
  vertical-align: top;
}

.legal-internal-back-to-top {
  position: fixed;
  right: 26px;
  bottom: 26px;
  z-index: 30;
  width: 46px;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: var(--legal-dark-blue);
  border: 0;
  border-radius: 50%;
  box-shadow: 0 10px 24px rgba(0, 52, 106, 0.22);
  cursor: pointer;
}

.legal-internal-back-to-top:hover,
.legal-internal-back-to-top:focus-visible {
  background: var(--legal-blue);
}

@media (max-width: 980px) {
  .legal-internal-layout {
    width: calc(100% - 32px);
    max-width: 760px;
  }

  .legal-internal-content {
    display: block;
  }

  .legal-internal-index {
    position: static;
    margin-bottom: 24px;
  }
}

@media (max-width: 640px) {
  .legal-internal-layout {
    width: calc(100% - 24px);
    padding: 24px 0 56px;
  }

  .legal-internal-hero h1 {
    font-size: 30px;
  }

  .legal-internal-document {
    padding: 26px 20px;
  }

  .legal-internal-section-index ul {
    column-count: 1;
  }
}
</style>
